/**
 * Domain detection utility for brand-specific features
 */

import { isBankTenantBrand, resolveTenantBranding } from '@/utils/tenantBranding';

export const getDomainInfo = () => {
  const hostname = window.location.hostname;
  
  return {
    hostname,
    isAtomicMarketplace: isCommerceLandingHost(hostname),
    isBotsLanding: isBotsLandingHost(hostname),
    isVaiuApp: hostname.includes('vaiu.app') && !hostname.includes('rbko.') && !hostname.includes('bnp.') && !hostname.includes('payformyfood.') && !isBotsLandingHost(hostname),
    isRBKo: hostname.includes('rbko.'),
    isBNP: hostname.includes('bnp.'),
    isPayForMyFood: hostname.includes('payformyfood.'),
    isBankApp: isBankAppHost(hostname),
    isSecurityTokenLanding: isSecurityTokenLandingHost(hostname),
  };
};

function viteAppDomain(): string {
  return (
    (import.meta as ImportMeta & { env?: { VITE_APP_DOMAIN?: string } }).env
      ?.VITE_APP_DOMAIN?.toLowerCase() ?? ''
  );
}

/** Agent / MCP docs home — bots.vaiu.app (or local `VITE_APP_DOMAIN=bots.vaiu.app`). */
export function isBotsLandingHost(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): boolean {
  const host = hostname.toLowerCase();
  const envDomain = viteAppDomain();

  if (envDomain.includes('bots.vaiu.app')) {
    return (
      host === 'bots.vaiu.app' ||
      host.endsWith('.bots.vaiu.app') ||
      host === 'localhost' ||
      host === '127.0.0.1'
    );
  }

  return host === 'bots.vaiu.app' || host.endsWith('.bots.vaiu.app');
}

/** Full commerce/marketplace landing — atomicMarketplace.com only. */
export function isCommerceLandingHost(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): boolean {
  const host = hostname.toLowerCase();
  if (isBotsLandingHost(hostname)) return false;

  if (viteAppDomain().includes('atomicmarketplace.com')) {
    return (
      host.includes('atomicmarketplace.com') ||
      host === 'localhost' ||
      host === '127.0.0.1'
    );
  }

  return host.includes('atomicmarketplace.com');
}

/** Compact security + token-tech landing — vaiu.app, bank apps, and other non-atomic hosts. */
export function isSecurityTokenLandingHost(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): boolean {
  return !isCommerceLandingHost(hostname) && !isBotsLandingHost(hostname);
}

/**
 * Default post-auth destination by hostname.
 * - atomicMarketplace.com → marketplace
 * - vaiu.app, bank apps, and other hosts → dashboard
 */
export function getDefaultAuthenticatedDestination(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): '/' | '/dashboard' | '/marketplace' {
  if (isBotsLandingHost(hostname)) return '/';
  return isCommerceLandingHost(hostname) ? '/marketplace' : '/dashboard';
}

/** Bank-branded buyer app (e.g. ing.banks.vaiu.app, bnp.banks.vaiu.app). */
export function isBankAppHost(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): boolean {
  const brandKey = resolveTenantBranding(hostname).brandKey;
  return isBankTenantBrand(brandKey);
}

export const getAppTitle = () => {
  const { isAtomicMarketplace, isBotsLanding } = getDomainInfo();

  if (isBotsLanding) {
    return 'Vaiu for bots';
  }

  if (isAtomicMarketplace) {
    return 'Atomic Marketplace';
  }
  
  return 'Vaiu';
};

export const getAppTagline = () => {
  const { isAtomicMarketplace, isBotsLanding, isVaiuApp, isBankApp } = getDomainInfo();

  if (isBotsLanding) {
    return 'Agent commerce on Vaiu';
  }

  if (isAtomicMarketplace) {
    return 'Decentralized Commerce Platform';
  }

  if (isBankApp || isVaiuApp) {
    return 'Secure tokenized payments';
  }
  
  return 'Digital Payment Platform';
};

export const getWelcomeMessage = () => {
  const { isAtomicMarketplace } = getDomainInfo();
  
  if (isAtomicMarketplace) {
    return 'Welcome to Atomic Marketplace';
  }
  
  return 'Welcome';
};

export const getHomePageHeading = () => {
  const { isAtomicMarketplace } = getDomainInfo();
  
  if (isAtomicMarketplace) {
    return {
      title: 'Welcome to The Atomic Marketplace',
      subtitle: 'Experience the future of tokenized atomic commerce. Buy, sell, and trade with tokenized security.'
    };
  }
  
  return {
    title: 'Build the Marketplace Make Money',
    subtitle: 'Request your favorite merchants to join and earn rewards for growing the ecosystem. Your voice shapes where you can pay.'
  };
};
